import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { ErrorBanner, Spinner } from "../components/common";
import RunInputs from "../components/RunInputs";
import { getUser, setUser } from "../lib/user";

const N_LCMS = 3;

/** A drop target that also works as a file picker. */
function DropZone({ id, label, hint, multiple, accept, files, onFiles }) {
  const [active, setActive] = useState(false);
  const filled = files.length > 0;

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div
        className={`dropzone${active ? " active" : ""}${filled ? " filled" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setActive(true);
        }}
        onDragLeave={() => setActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setActive(false);
          onFiles([...e.dataTransfer.files]);
        }}
        onClick={() => document.getElementById(id).click()}
      >
        {filled ? (
          <>
            <strong>{files.length} file{files.length === 1 ? "" : "s"} ready</strong>
            <ul className="file-list">
              {files.map((f) => (
                <li key={f.name} className="mono">
                  {f.name}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <>
            Drop {multiple ? "files" : "a file"} here, or click to choose.
            <div style={{ fontSize: "0.85em" }}>{hint}</div>
          </>
        )}
      </div>
      <input
        id={id}
        type="file"
        multiple={multiple}
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => onFiles([...e.target.files])}
      />
    </div>
  );
}

/**
 * Step 1. Four files in, a reconstructed mapping out.
 *
 * The run is analysed on upload and rejected outright if the files do not form a
 * valid set, so reaching the review panel below already means the series in the
 * instrument files and the rows in the Substanzen sheet agree.
 */
export default function UploadScreen() {
  const navigate = useNavigate();
  const [lcmsFiles, setLcmsFiles] = useState([]);
  const [substanzen, setSubstanzen] = useState([]);
  const [createdBy, setCreatedBy] = useState(getUser());
  const [title, setTitle] = useState("");
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);

  const ready = lcmsFiles.length === N_LCMS && substanzen.length === 1;

  async function upload() {
    setError(null);
    setProgress(0);
    try {
      setUser(createdBy);
      const result = await api.createRun({
        lcmsFiles,
        substanzenFile: substanzen[0],
        createdBy: createdBy || null,
        title: title || null,
        onProgress: setProgress,
      });
      setDetail(result);
    } catch (e) {
      setError(e);
    } finally {
      setProgress(null);
    }
  }

  if (detail) {
    const { manifest, mapping, conditions, n_observations: nObservations } = detail;
    return (
      <>
        <div className="banner ok">
          <strong>Files accepted.</strong> {nObservations} injections read across{" "}
          {conditions.length} conditions. Check the mapping below before continuing — every
          result downstream depends on it.
        </div>

        <RunInputs manifest={manifest} mapping={mapping} />

        <div className="button-row">
          <button className="primary" onClick={() => navigate(`/runs/${manifest.run_id}/qc`)}>
            Mapping is correct — continue to QC
          </button>
          <button onClick={() => setDetail(null)}>Upload a different set</button>
        </div>
      </>
    );
  }

  return (
    <>
      <ErrorBanner error={error} onDismiss={() => setError(null)} />
      <div className="card" style={{ maxWidth: 720 }}>
        <h2>New run</h2>
        <p className="card-note">
          Three LC-MS/MS exports — co-incubation, pre-incubation −NADPH and pre-incubation
          +NADPH — plus the Substanzen workbook. The condition of each export is read from the
          instrument file itself and checked against its filename.
        </p>

        <DropZone
          id="lcms-input"
          label="Instrument files (3 × .txt)"
          hint="…_CYP3A4Co.txt, …_CYP3A4Vo.txt, …_CYP3A4V+.txt"
          multiple
          accept=".txt,text/plain"
          files={lcmsFiles}
          onFiles={(files) => setLcmsFiles(files.slice(0, N_LCMS))}
        />

        <DropZone
          id="substanzen-input"
          label="Substanzen workbook (.xlsx)"
          hint="Compound identities, assay concentrations and run metadata"
          accept=".xlsx"
          files={substanzen}
          onFiles={(files) => setSubstanzen(files.slice(0, 1))}
        />

        <div className="field">
          <label htmlFor="title">Title (optional)</label>
          <input
            id="title"
            type="text"
            value={title}
            placeholder="e.g. CYP3A4 screen, December plate 1"
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="created-by">Your name</label>
          <input
            id="created-by"
            type="text"
            value={createdBy}
            placeholder="Recorded against this run and against every exclusion"
            onChange={(e) => setCreatedBy(e.target.value)}
          />
        </div>

        <div className="button-row">
          <button className="primary" disabled={!ready || progress !== null} onClick={upload}>
            Upload and analyse
          </button>
          {progress !== null && <Spinner label={`Uploading… ${progress} %`} />}
          {!ready && progress === null && (
            <span className="muted">
              {N_LCMS - lcmsFiles.length > 0
                ? `${N_LCMS - lcmsFiles.length} more instrument file(s) needed`
                : "Add the Substanzen workbook"}
            </span>
          )}
        </div>
      </div>
    </>
  );
}
